import React, { CSSProperties, FC } from 'react'
import { HeroImageProps } from '@sport1/types/web'
import NonFlexingContainer from '@sport1/news-styleguide/NonFlexingContainer'
import Head from 'next/head'
import { spacings } from '@sport1/news-styleguide/spacings'
import ArticleTeaser from '@sport1/news-styleguide/ArticleTeaser'
import Font from '@sport1/news-styleguide/Font'
import colorPattern from '@sport1/news-styleguide/colorPattern'

import {
    defaultAdditionalWidths,
    transformImageUrlToSize,
    transformImageUrlToSrcSet,
} from '@/helpers/imageHelper'
import { useTopMediaDimensions } from '@/hooks/useTopMediaDimensions'
import {
    TOP_MEDIA_MAX_HEIGHT,
    TOP_MEDIA_MAX_WIDTH,
    TOP_MEDIA_MAX_WIDTH_MOBILE,
} from '@/utils/content/constants'
import { sport1Regular } from '@/helpers/fonts'

type Props = {
    content: HeroImageProps
}

const textStyle = {
    backgroundImage: 'linear-gradient(to top, rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0))',
    bottom: '0',
    padding: spacings['spacing-6'],
    position: 'absolute',
    width: '100%',
} as CSSProperties

const ArticleTopImage: FC<Props> = ({ content }) => {
    const { width, height } = useTopMediaDimensions()
    const src = transformImageUrlToSize(content.imageUrl, width, height)

    const additionalWidths = [TOP_MEDIA_MAX_WIDTH_MOBILE, ...defaultAdditionalWidths]
        .sort()
        .reverse()
    const srcSet = transformImageUrlToSrcSet(
        content.imageUrl,
        TOP_MEDIA_MAX_WIDTH,
        TOP_MEDIA_MAX_HEIGHT,
        additionalWidths //always include all 4 sizes in the srcSet. Browser selects the best one.
    )

    const ratio = TOP_MEDIA_MAX_WIDTH / TOP_MEDIA_MAX_HEIGHT
    const dimensionOptions = additionalWidths.map(w => [w, Math.floor(w / ratio)])
    dimensionOptions.unshift([TOP_MEDIA_MAX_WIDTH, TOP_MEDIA_MAX_HEIGHT]) //all 4 widths and heights

    return (
        <>
            <Head>
                {dimensionOptions.map(([width, height], index) => {
                    //for each size create a preload link with media query
                    let mediaQuery = ''
                    if (index < dimensionOptions.length - 1) {
                        mediaQuery += `(min-width: ${dimensionOptions[index + 1][0] + 1}px)`
                    }
                    if (index > 0) {
                        mediaQuery += `${mediaQuery ? ' and ' : ''}(max-width: ${width}px)`
                    }
                    const href = transformImageUrlToSize(content.imageUrl, width, height)
                    return (
                        <link
                            rel="preload"
                            fetchpriority="high"
                            href={href}
                            key={href}
                            as="image"
                            media={mediaQuery}
                        />
                    )
                })}
            </Head>
            <NonFlexingContainer
                className="article-top-image-wrapper"
                position="relative"
                testID="article-top-image"
            >
                <ArticleTeaser
                    type="TopImage"
                    size="M"
                    imageUrlArray={[{ src, srcSet, alt: content.description }]}
                />
                <div style={textStyle}>
                    <Font
                        nextFontFamilyVariant={sport1Regular.style.fontFamily}
                        color={colorPattern.pearl}
                        fontSize={['font-size-3', 'font-size-4', 'font-size-4']}
                        lineHeight={['lineHeight-20', 'lineHeight-22', 'lineHeight-22']}
                    >
                        {content.title}
                    </Font>
                    <Font
                        nextFontFamilyVariant={sport1Regular.style.fontFamily}
                        color={colorPattern.pearl}
                        fontSize={['font-size-3', 'font-size-4', 'font-size-4']}
                        lineHeight={['lineHeight-20', 'lineHeight-22', 'lineHeight-22']}
                    >{`© ${content.copyright}`}</Font>
                </div>
            </NonFlexingContainer>
        </>
    )
}

export default ArticleTopImage
